import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { MessageCircle, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { getStoreByOwner, getOrdersByStore, updateOrderStatus } from '../lib/firebaseService';

export default function Orders() {
  const { firebaseUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (firebaseUser) {
      loadData();
    }
  }, [firebaseUser]);

  const loadData = async () => {
    try {
      setLoading(true);
      const userStore = await getStoreByOwner(firebaseUser.uid);
      setStore(userStore);
      
      if (userStore) {
        const ordersList = await getOrdersByStore(userStore.id);
        setOrders(ordersList);
      }
    } catch (error) {
      console.error('Məlumatlar yüklənə bilmədi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsApp = (order) => {
    if (!store?.whatsapp_number) {
      toast.error('WhatsApp nömrəsi təyin edilməyib');
      return;
    }

    const items = order.items?.map(item => `${item.product_name} x${item.quantity} - ${item.price} AZN`).join('\n') || '';
    const message = `Sifariş #${order.id.slice(0, 8)}\n\nMüştəri: ${order.customer_name}\nTelefon: ${order.customer_phone}\n\nMəhsullar:\n${items}\n\nCəmi: ${order.total} AZN${order.notes ? `\n\nQeydlər: ${order.notes}` : ''}`;
    
    const whatsappUrl = `https://wa.me/${store.whatsapp_number.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleStatusUpdate = async (orderId, status) => {
    try {
      await updateOrderStatus(orderId, status);
      toast.success('Status yeniləndi');
      loadData();
    } catch (error) {
      toast.error('Əməliyyat uğursuz oldu');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    try {
      if (timestamp.toDate) {
        return format(timestamp.toDate(), 'dd.MM.yyyy HH:mm');
      }
      return format(new Date(timestamp), 'dd.MM.yyyy HH:mm');
    } catch {
      return '-';
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { label: 'Gözləyir', color: 'bg-orange-100 text-orange-700', icon: Clock },
      completed: { label: 'Tamamlandı', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      cancelled: { label: 'İmtina', color: 'bg-red-100 text-red-700', icon: XCircle },
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Yüklənir...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!store) {
    return (
      <DashboardLayout>
        <div className="space-y-6" data-testid="orders-page">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-yellow-800 mb-1">Mağaza yaradılmayıb</h3>
                <p className="text-sm text-yellow-700 mb-3">
                  Sifarişləri görmək üçün əvvəlcə mağazanızı yaratmalısınız.
                </p>
                <a
                  href="/dashboard/store"
                  className="inline-flex items-center justify-center h-10 px-6 bg-yellow-600 text-white rounded-lg font-medium hover:bg-yellow-700 transition-all"
                >
                  Mağaza yarat
                </a>
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="orders-page">
        <div>
          <h1 className="text-3xl font-bold">Sifarişlər</h1>
          <p className="text-muted-foreground mt-1">Müştəri sifarişlərini idarə edin</p>
        </div>

        {orders.length === 0 ? (
          <div className="bg-white rounded-xl border border-border p-12 text-center" data-testid="no-orders">
            <p className="text-muted-foreground">Heç bir sifariş yoxdur.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div
                key={order.id}
                className="bg-white rounded-xl border border-border p-6 hover:shadow-md transition-shadow"
                data-testid={`order-card-${order.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg">Sifariş #{order.id.slice(0, 8)}</h3>
                      {getStatusBadge(order.status)}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(order.created_at)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary">{order.total} AZN</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Müştəri</p>
                    <p className="font-medium">{order.customer_name}</p>
                    <p className="text-sm text-muted-foreground">{order.customer_phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Məhsullar</p>
                    {order.items?.map((item, idx) => (
                      <p key={idx} className="text-sm">
                        {item.product_name} x{item.quantity} - {item.price} AZN
                      </p>
                    ))}
                  </div>
                </div>

                {order.notes && (
                  <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-1">Qeydlər</p>
                    <p className="text-sm">{order.notes}</p>
                  </div>
                )}

                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={() => handleWhatsApp(order)}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid={`whatsapp-button-${order.id}`}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp-da aç
                  </Button>
                  {order.status === 'pending' && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => handleStatusUpdate(order.id, 'completed')}
                        data-testid={`complete-button-${order.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Tamamla
                      </Button>
                      <Button
                        variant="outline"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleStatusUpdate(order.id, 'cancelled')}
                        data-testid={`cancel-button-${order.id}`}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        İmtina et
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
